/**
 * DotMap — base class for the dot-map Web Component family.
 *
 * Phase 2: layered render pipeline (SVG identity + Canvas FX overlay).
 * Phase 3: RAF-driven canvas FX with RippleEngine + cross-instance DotMapBus.
 * Phase 5a: roles JSON + per-dot role coloring + row trim.
 * Phase 5b: viewBox alignment (territory native viewBox = canonical, dots scaled to fit).
 * Phase 5c: role-color pulse (CSS filter brightness, preserves role hex).
 * Phase 5d: role-color ripple (clicked dot's role hex flows to ripple).
 * Phase 5e: diaspora ring (88 deterministic-position halo dots, role 0).
 *
 *   <dot-map-pr> Shadow DOM:
 *     ┌────────────────────────────────────┐
 *     │ <svg.layer-territory> silhouette  │  ← DOM identity layer
 *     │ <svg.layer-dots> N <circle>       │     (native click/hover/ARIA)
 *     │   - role-colored fills            │
 *     │   - .is-pulsing on ~10% subset    │
 *     │   - .is-diaspora ring outside     │
 *     ├────────────────────────────────────┤
 *     │ <canvas.layer-fx> RAF ripple wave │  ← FX overlay (1 draw call/frame)
 *     ├────────────────────────────────────┤
 *     │ <slot name="controls" />           │
 *     │ <slot name="legend" />             │
 *     └────────────────────────────────────┘
 *
 * Per stu_plan--epl-dot-map-component-v3.md.
 */

import { LitElement, html, svg, css, type PropertyValues } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { classMap } from 'lit/directives/class-map.js';
import { RippleEngine, type RippleSpec } from '../fx/ripple-engine';
import { getDotMapBus, type DotMapBusEventDetail } from '../lib/dot-map-bus';

interface AnchorPoint {
  col: number;
  row: number;
  x: number;
  y: number;
  /** Assigned at load time per population_distribution. 0 = diaspora (off-island). */
  roleId?: number;
  /** True for diaspora ring dots (outside silhouette). */
  isDiaspora?: boolean;
}

interface AnchorPayload {
  meta: { map_w: number; map_h: number; count: number; grid: number };
  anchors: AnchorPoint[];
}

interface Role {
  id: number;
  slug: string;
  name: string;
  hex: string;
}

interface RolesPayload {
  meta: {
    silhouette_max_row?: number;
    diaspora_count?: number;
    [k: string]: unknown;
  };
  roles: Role[];
  population_distribution: Record<string, number>;
}

type ViewBox = [number, number, number, number];

export class DotMap extends LitElement {
  // ─── Public reactive attributes (per v3 §1 API) ──────────────────────────

  @property({ type: String, reflect: true })
  state: 'overview' | 'personalized' | 'visitor' | 'filtered' = 'overview';

  @property({ type: String, reflect: true })
  theme: 'light' | 'dark' = 'light';

  @property({ type: String, attribute: 'user-role' })
  userRole: string = '';

  @property({ type: String, attribute: 'user-pueblo' })
  userPueblo: string = '';

  @property({ type: String })
  filter: string = '';

  @property({ type: Boolean, attribute: 'show-controls' })
  showControls = true;

  @property({ type: Boolean, attribute: 'show-legend' })
  showLegend = true;

  @property({ type: Boolean, attribute: 'cross-tab' })
  crossTab = false;

  @property({ type: String, attribute: 'data-src' })
  dataSrc: string = '';

  @property({ type: String, attribute: 'roles-src' })
  rolesSrc: string = '';

  @property({ type: String, attribute: 'geography-src' })
  geographySrc: string = '';

  // ─── Subclass overrides ──────────────────────────────────────────────────

  static defaultDataSrc = '';
  static defaultGeographySrc = '';
  static defaultRolesSrc = '';

  // ─── Internal state ──────────────────────────────────────────────────────

  @state() private _territoryPaths: string | null = null;
  @state() private _anchors: AnchorPoint[] = [];
  @state() private _diaspora: AnchorPoint[] = [];
  @state() private _roles: Role[] = [];
  @state() private _rolesById = new Map<number, Role>();
  /** Geo viewBox = territory's native viewBox (silhouette source-of-truth).
   *  Phase 7.5 default = canonical 784.47 x 252.02 from regenerated dotmap-pr.svg
   *  source (aspect 3.113 — matches anchors). Replaces stale 1719.25 x 568.62
   *  legacy default (aspect 3.024) which caused outline-vs-anchor squish. */
  @state() private _geoViewBox: ViewBox = [0, 0, 784.47, 252.02];
  /** Anchor coordinate space — Phase 7.5: matches geoViewBox so scale = 1. */
  @state() private _anchorViewBox: ViewBox = [0, 0, 784.47, 252.02];
  /** Canonical viewBox = canvas render space. Extended beyond geo viewBox so silhouette
   *  occupies SILHOUETTE_WIDTH_RATIO of canvas width (default 60%), leaving 40% whitespace
   *  around for the diaspora ring to render without clamping to silhouette edges. */
  @state() private _canonicalViewBox: ViewBox = [0, 0, 784.47, 252.02];
  /** Silhouette translate offset (centered in canonical viewBox). */
  @state() private _silhouetteOffset: { x: number; y: number } = { x: 0, y: 0 };
  @state() private _ready = false;
  @state() private _error: string | null = null;

  @query('canvas.layer-fx') private _fxCanvas?: HTMLCanvasElement;

  private _resizeObserver?: ResizeObserver;
  private _rafId = 0;
  private _ripples = new RippleEngine();
  private _busUnsubs: Array<() => void> = [];

  // ─── Styles ──────────────────────────────────────────────────────────────

  static styles = css`
    :host {
      --dot-map-bg: var(--dot-map-bg-override, transparent);
      --dot-map-territory-fill: var(--dot-map-territory-fill-override, rgba(14, 17, 21, 0.06));
      --dot-map-dot-color: var(--dot-map-dot-color-override, rgba(14, 17, 21, 0.55));
      --dot-map-dot-radius: var(--dot-map-dot-radius-override, 3.2);
      --dot-map-diaspora-radius: var(--dot-map-diaspora-radius-override, 2.4);
      --dot-map-diaspora-opacity: var(--dot-map-diaspora-opacity-override, 0.55);
      --dot-map-pulse-scale: var(--dot-map-pulse-scale-override, 1.45);
      --dot-map-pulse-brightness: var(--dot-map-pulse-brightness-override, 1.4);
      --dot-map-pulse-saturate: var(--dot-map-pulse-saturate-override, 1.3);
      --dot-map-ripple-color: var(--dot-map-ripple-color-override, rgba(0, 102, 255, 0.85));
      /* Phase 7.5: aspect-ratio reflects the EXTENDED canonical viewBox (silhouette =
         60% of width per Pedro spec → canonical aspect ~ silhouette aspect, since
         canonical is silhouette uniformly extended). Fallback assumes silhouette
         viewBox 784.47 × 252.02 (regen from dotmap-pr.svg) → canonical aspect 3.113.
         Host CSS can override via --dot-map-aspect-override. */
      --dot-map-aspect: var(--dot-map-aspect-override, calc(784.47 / 252.02));

      display: block;
      width: 100%;
      aspect-ratio: var(--dot-map-aspect);
      /* Default desktop cap so the widget doesn't overflow huge viewports.
         Pedro spec: silhouette = 60% of viewport. At 1920px viewport, silhouette = 1152px,
         canonical (silhouette / 0.60) = 1920px ≈ 120rem. Host can override. */
      max-width: var(--dot-map-max-width-override, 120rem);
      margin-inline: auto;
      background: var(--dot-map-bg);
      position: relative;
      box-sizing: border-box;
      overflow: hidden;
      font-family: var(--dot-map-font, system-ui, -apple-system, sans-serif);
    }

    :host([theme='dark']) {
      --dot-map-territory-fill: rgba(236, 238, 240, 0.08);
      --dot-map-dot-color: rgba(236, 238, 240, 0.55);
      --dot-map-ripple-color: rgba(236, 238, 240, 0.85);
    }

    .stage { position: absolute; inset: 0; }

    svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
    }

    .layer-territory path,
    .layer-territory g > * {
      fill: var(--dot-map-territory-fill) !important;
    }

    .layer-dots circle {
      fill: var(--dot-map-dot-color);
      transform-origin: center;
      transform-box: fill-box;
      transition: transform 220ms cubic-bezier(0.5, 0, 0.2, 1),
                  filter 220ms ease;
      cursor: pointer;
    }
    .layer-dots circle:hover {
      filter: brightness(var(--dot-map-pulse-brightness)) saturate(var(--dot-map-pulse-saturate));
      transform: scale(1.4);
    }

    /* Pulse keyframe brightens via filter so the role color hex on the dot is
       preserved (vs swapping fill to a fixed pulse color). */
    .layer-dots circle.is-pulsing {
      animation: dot-pulse 8s cubic-bezier(0.5, 0, 0.2, 1) infinite;
    }

    @keyframes dot-pulse {
      0%, 88%, 100% {
        transform: scale(1);
        filter: brightness(1) saturate(1);
      }
      92% {
        transform: scale(var(--dot-map-pulse-scale));
        filter: brightness(var(--dot-map-pulse-brightness))
                saturate(var(--dot-map-pulse-saturate));
      }
    }

    /* Diaspora ring — smaller + dimmer to read as off-island representation. */
    .layer-dots circle.is-diaspora {
      opacity: var(--dot-map-diaspora-opacity);
      cursor: default;
    }
    .layer-dots circle.is-diaspora:hover {
      transform: none;
      filter: none;
    }

    canvas.layer-fx {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    ::slotted([slot='controls']),
    ::slotted([slot='legend']) {
      position: absolute;
      z-index: 2;
    }

    .status {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      font-family: ui-monospace, monospace;
      font-size: 0.85rem;
      color: var(--dot-map-dot-color);
      opacity: 0.7;
    }
  `;

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  connectedCallback(): void {
    super.connectedCallback();
    this._loadData();
    this._wireBus();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopRaf();
    this._resizeObserver?.disconnect();
    this._busUnsubs.forEach((un) => un());
    this._busUnsubs = [];
  }

  protected firstUpdated(_changed: PropertyValues): void {
    // Canvas doesn't exist yet (loading state shows .status div).
    // Real init happens in updated() once _ready flips and canvas mounts.
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('_ready') && this._ready) {
      // RippleEngine works in the canonical viewBox (extended canvas, includes
      // silhouette + diaspora whitespace region).
      this._ripples.specWidth = this._canonicalViewBox[2];
      this._ripples.specHeight = this._canonicalViewBox[3];
      this._sizeCanvas();
      if (this._fxCanvas && !this._resizeObserver) {
        this._resizeObserver = new ResizeObserver(() => this._sizeCanvas());
        this._resizeObserver.observe(this._fxCanvas);
      }
    }
  }

  // ─── Data loading ────────────────────────────────────────────────────────

  private async _loadData(): Promise<void> {
    const ctor = this.constructor as typeof DotMap;
    const dataUrl = this.dataSrc || ctor.defaultDataSrc;
    const geoUrl = this.geographySrc || ctor.defaultGeographySrc;
    const rolesUrl = this.rolesSrc || ctor.defaultRolesSrc;

    if (!dataUrl || !geoUrl) {
      this._error = 'Missing dataSrc or geographySrc — set via attribute or subclass default.';
      return;
    }

    try {
      const [geoText, anchorJson, rolesJson] = await Promise.all([
        fetch(geoUrl).then((r) => {
          if (!r.ok) throw new Error(`geography fetch ${r.status}`);
          return r.text();
        }),
        fetch(dataUrl).then((r) => {
          if (!r.ok) throw new Error(`anchors fetch ${r.status}`);
          return r.json() as Promise<AnchorPayload>;
        }),
        rolesUrl
          ? fetch(rolesUrl).then((r) => (r.ok ? (r.json() as Promise<RolesPayload>) : null))
          : Promise.resolve(null),
      ]);

      this._territoryPaths = extractSvgInner(geoText);
      this._geoViewBox = parseSvgViewBox(geoText) ?? [0, 0, 784.47, 252.02];
      this._anchorViewBox = [0, 0, anchorJson.meta.map_w, anchorJson.meta.map_h];

      // Compute canonical viewBox: silhouette occupies SILHOUETTE_WIDTH_RATIO of width
      // (default 60% per Pedro's spec), leaving the remainder as whitespace around for
      // diaspora ring + breathing room. Height extended proportionally so silhouette
      // aspect is preserved.
      const SILHOUETTE_WIDTH_RATIO = 0.60;
      const [, , gw, gh] = this._geoViewBox;
      const cw = gw / SILHOUETTE_WIDTH_RATIO;
      const ch = gh * (cw / gw);
      this._canonicalViewBox = [0, 0, cw, ch];
      this._silhouetteOffset = { x: (cw - gw) / 2, y: (ch - gh) / 2 };

      // Apply roles + row trim if roles payload loaded.
      if (rolesJson) {
        this._roles = rolesJson.roles;
        this._rolesById = new Map(rolesJson.roles.map((r) => [r.id, r]));
        const maxRow = rolesJson.meta.silhouette_max_row ?? Infinity;
        const trimmed = anchorJson.anchors.filter((a) => a.row <= maxRow);
        this._anchors = assignRoles(trimmed, rolesJson.population_distribution);
        // Generate diaspora ring (Phase 5e). Lives in canonical viewBox coords so
        // dots can render in the whitespace region around the silhouette without
        // being clamped to silhouette edges. Phase 7: pass silhouette dimensions
        // so radial values are tuned to the silhouette/canvas ratio.
        const diasporaCount = rolesJson.meta.diaspora_count ?? 0;
        if (diasporaCount > 0) {
          this._diaspora = generateDiaspora(
            this._canonicalViewBox,
            diasporaCount,
            { w: this._geoViewBox[2], h: this._geoViewBox[3] },
          );
        }
      } else {
        this._anchors = anchorJson.anchors;
      }

      this._ready = true;
      this.dispatchEvent(
        new CustomEvent('map-ready', {
          detail: {
            count: this._anchors.length,
            diasporaCount: this._diaspora.length,
            roleCount: this._roles.length,
            geoViewBox: this._geoViewBox,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (e) {
      this._error = (e as Error).message;
      // eslint-disable-next-line no-console
      console.error('[dot-map] load failed:', e);
    }
  }

  // ─── Bus wiring ──────────────────────────────────────────────────────────

  private _wireBus(): void {
    const bus = getDotMapBus();
    if (this.crossTab) bus.enableCrossTab();

    this._busUnsubs.push(
      bus.on('fire-ripple', (detail: DotMapBusEventDetail) => {
        const { x, y, color, life, maxRadius } = detail as Partial<RippleSpec>;
        if (typeof x === 'number' && typeof y === 'number') {
          this.fireRipple({ x, y, color, life, maxRadius });
        }
      }),
    );

    // member-joined → island-wide ripple from geo center, tinted by role color.
    // Phase 7 contract: role-cards Phase D fires this after user-dot lands.
    this._busUnsubs.push(
      bus.on('member-joined', (detail: DotMapBusEventDetail) => {
        const d = detail as { role?: string; color?: string; pueblo?: string };
        const [vx, vy, vw, vh] = this._geoViewBox;
        const cx = vx + vw / 2;
        const cy = vy + vh / 2;
        // Resolve color: explicit > role lookup > CSS default
        let color = d.color;
        if (!color && d.role) {
          color = this._roles.find((r) => r.slug === d.role)?.hex;
        }
        this.fireRipple({
          x: cx,
          y: cy,
          color,
          life: 1800,
          maxRadius: Math.max(vw, vh) * 0.85,
        });
      }),
    );
  }

  // ─── Canvas FX layer ─────────────────────────────────────────────────────

  private _sizeCanvas(): void {
    const canvas = this._fxCanvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private _startRaf(): void {
    if (this._rafId) return;
    const tick = (t: number) => {
      const canvas = this._fxCanvas;
      if (!canvas) {
        this._rafId = 0;
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this._rafId = 0;
        return;
      }
      const stillAlive = this._ripples.draw(ctx, canvas.width, canvas.height, t);
      if (stillAlive) {
        this._rafId = requestAnimationFrame(tick);
      } else {
        this._rafId = 0;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopRaf(): void {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = 0;
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  fireRipple(spec: RippleSpec): number {
    if (!spec.color) {
      const c = getComputedStyle(this).getPropertyValue('--dot-map-ripple-color').trim();
      if (c) spec.color = c;
    }
    if (this._fxCanvas && (this._fxCanvas.width <= 1 || this._fxCanvas.height <= 1)) {
      this._sizeCanvas();
    }
    const id = this._ripples.fire(spec);
    this._startRaf();
    return id;
  }

  on(name: string, handler: (detail: DotMapBusEventDetail) => void): () => void {
    return getDotMapBus().on(name, handler);
  }

  // ─── Dot click handler — Phase 5d uses the dot's role color for the ripple. ─

  private _onDotClick = (e: Event): void => {
    const target = e.target as SVGCircleElement;
    if (!(target instanceof SVGCircleElement)) return;
    if (target.classList.contains('is-diaspora')) return; // diaspora dots are decorative
    const x = Number(target.getAttribute('cx'));
    const y = Number(target.getAttribute('cy'));
    if (Number.isNaN(x) || Number.isNaN(y)) return;
    const roleSlug = target.dataset.role;
    const role = this._roles.find((r) => r.slug === roleSlug);
    this.fireRipple({ x, y, color: role?.hex });
    this.dispatchEvent(
      new CustomEvent('dot-click', {
        detail: {
          slug: roleSlug,
          col: Number(target.dataset.col),
          row: Number(target.dataset.row),
          idx: Number(target.dataset.idx),
          x,
          y,
          role: role ?? null,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  protected render() {
    if (this._error) {
      return html`<div class="status">⚠ ${this._error}</div>`;
    }
    if (!this._ready) {
      return html`<div class="status">loading…</div>`;
    }

    const [, , gw, gh] = this._geoViewBox;
    const [, , aw, ah] = this._anchorViewBox;
    const [, , cw, ch] = this._canonicalViewBox;
    const { x: tx_silo, y: ty_silo } = this._silhouetteOffset;
    const viewBoxAttr = `0 0 ${cw} ${ch}`;
    const radius = Number(getComputedStyle(this).getPropertyValue('--dot-map-dot-radius') || 7);
    const diasporaRadius = Number(
      getComputedStyle(this).getPropertyValue('--dot-map-diaspora-radius') || 5,
    );

    // Phase 7.5: outline + anchors regenerated from canonical dotmap-pr.svg source
    // (single coord system, viewBox 784.47 × 252.02, aspect 3.113). Uniform-scale
    // path retained for safety — when geo + anchor viewBoxes match (default), scale=1
    // and there's zero letterbox. Subclasses passing custom data still get the same
    // safety behavior they had under v3.
    const scaleX = gw / aw;
    const scaleY = gh / ah;
    const uniformScale = Math.min(scaleX, scaleY);
    const aw_scaled = aw * uniformScale;
    const ah_scaled = ah * uniformScale;
    const tx_dots = tx_silo + (gw - aw_scaled) / 2;
    const ty_dots = ty_silo + (gh - ah_scaled) / 2;
    const dotsTransform = `translate(${tx_dots} ${ty_dots}) scale(${uniformScale})`;
    const territoryTransform = `translate(${tx_silo} ${ty_silo})`;

    return html`
      <div class="stage" part="stage">
        <svg
          class="layer-territory"
          viewBox=${viewBoxAttr}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <g transform=${territoryTransform}>
            ${unsafeSVG(this._territoryPaths ?? '')}
          </g>
        </svg>

        <svg
          class="layer-dots"
          viewBox=${viewBoxAttr}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Map dot grid"
          @click=${this._onDotClick}
        >
          <g transform=${dotsTransform}>
            ${this._anchors.map((a, i) => {
              const isPulsing = i % 10 === 0;
              const delayPart = isPulsing ? `animation-delay:${(i * 137.5) % 8000}ms;` : '';
              const role = a.roleId !== undefined ? this._rolesById.get(a.roleId) : undefined;
              const fillPart = role ? `fill:${role.hex};` : '';
              const styleAttr = `${fillPart}${delayPart}`;
              return svg`
                <circle
                  cx=${a.x}
                  cy=${a.y}
                  r=${radius}
                  class=${classMap({ 'is-pulsing': isPulsing })}
                  style=${styleAttr || null}
                  data-col=${a.col}
                  data-row=${a.row}
                  data-idx=${i}
                  data-role=${role?.slug ?? ''}
                />
              `;
            })}
          </g>
          ${this._diaspora.length
            ? svg`
              <g class="diaspora-group">
                ${this._diaspora.map((d, i) => {
                  const role = this._rolesById.get(0);
                  return svg`
                    <circle
                      cx=${d.x}
                      cy=${d.y}
                      r=${diasporaRadius}
                      class="is-diaspora"
                      style=${role ? `fill:${role.hex};` : null}
                      data-idx=${`d${i}`}
                      data-role="diaspora"
                    />
                  `;
                })}
              </g>
            `
            : null}
        </svg>

        <canvas class="layer-fx" aria-hidden="true"></canvas>

        ${this.showControls ? html`<slot name="controls"></slot>` : null}
        ${this.showLegend ? html`<slot name="legend"></slot>` : null}
      </div>
    `;
  }
}

// ─── SVG parsing helpers ───────────────────────────────────────────────────

function extractSvgInner(svgText: string): string {
  const openMatch = svgText.match(/<svg\b[^>]*>/i);
  const closeMatch = svgText.lastIndexOf('</svg>');
  if (!openMatch || closeMatch < 0) return svgText;
  const inner = svgText.slice(openMatch.index! + openMatch[0].length, closeMatch);
  return inner.replace(/<defs[\s\S]*?<\/defs>/gi, '');
}

function parseSvgViewBox(svgText: string): ViewBox | null {
  const m = svgText.match(/<svg\b[^>]*\sviewBox=["']([^"']+)["']/i);
  if (!m) return null;
  const parts = m[1].trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
  return parts as ViewBox;
}

/** Distribute role IDs across anchors per population weights, then Fisher-Yates
 *  shuffle so the role distribution is geographically random across the silhouette. */
function assignRoles(anchors: AnchorPoint[], distribution: Record<string, number>): AnchorPoint[] {
  const target = anchors.length;
  const distrib = { ...distribution };
  const sumDist = Object.values(distrib).reduce((a, b) => a + b, 0);
  const diff = sumDist - target;
  if (diff !== 0 && distrib['1'] !== undefined) distrib['1'] -= diff;

  const roleAssignments: number[] = [];
  for (const [roleIdStr, count] of Object.entries(distrib)) {
    const id = parseInt(roleIdStr, 10);
    for (let j = 0; j < count; j++) roleAssignments.push(id);
  }
  for (let i = roleAssignments.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [roleAssignments[i], roleAssignments[j]] = [roleAssignments[j], roleAssignments[i]];
  }
  return anchors.map((a, i) => ({ ...a, roleId: roleAssignments[i] ?? 1 }));
}

/** Mulberry32 — deterministic PRNG so diaspora positions are stable across renders. */
function pseudoRandom(seed: number): number {
  let t = (seed + 0x6D2B79F5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Phase 5e + Phase 7 — generate diaspora ring positions in canonical viewBox coords.
 *  Two interleaved bands (kissing edge + outer halo) using deterministic seeds.
 *
 *  Phase 7: takes optional silhouette dimensions so radial values place dots in the
 *  whitespace BETWEEN silhouette edge and canonical edge — no clamping to silhouette
 *  edges. If silhouette omitted, falls back to legacy radial values for backwards-compat.
 */
function generateDiaspora(
  canonicalViewBox: ViewBox,
  count: number,
  silhouetteSize?: { w: number; h: number },
): AnchorPoint[] {
  const [, , cw, ch] = canonicalViewBox;
  const cx = cw / 2;
  const cy = ch / 2;
  const out: AnchorPoint[] = [];

  // Compute radial range so dots land between silhouette edge and canonical edge.
  // If silhouette omitted (legacy), use the original 0.55–0.70 range.
  let kissBase = 0.55, kissJitter = 0.05;
  let outerBase = 0.62, outerJitter = 0.08;
  if (silhouetteSize) {
    // silhouette half-extent as fraction of canonical width
    const siloHalfFracX = silhouetteSize.w / cw / 2;  // e.g. 0.30 for 60% silhouette
    // Place kiss-edge band JUST outside silhouette edge (silhouette/2 + small buffer).
    // Place outer band 5-8% further out, capping at 0.48 (just inside canonical edge).
    kissBase = Math.min(siloHalfFracX + 0.02, 0.40);
    kissJitter = 0.03;
    outerBase = Math.min(siloHalfFracX + 0.07, 0.45);
    outerJitter = 0.03;
  }

  for (let i = 0; i < count; i++) {
    // Slightly off-axis start angle + golden-ratio increment so positions
    // distribute well even with small `count`.
    const angle = (i * 2.39996) % (Math.PI * 2); // golden angle radians
    const kissEdge = i % 2 === 0;
    const seed = pseudoRandom(i * 2654435761 + 7);
    // Elliptical halo radii expressed as fraction of canonical half-extent.
    const radial = kissEdge
      ? kissBase + seed * kissJitter
      : outerBase + seed * outerJitter;
    const x = cx + Math.cos(angle) * cw * radial;
    const y = cy + Math.sin(angle) * ch * radial;
    // Clamp to canonical viewBox so dots don't get clipped to nothing.
    const cxOut = Math.max(8, Math.min(cw - 8, x));
    const cyOut = Math.max(8, Math.min(ch - 8, y));
    out.push({
      col: -1,
      row: -1,
      x: cxOut,
      y: cyOut,
      roleId: 0,
      isDiaspora: true,
    });
  }
  return out;
}
